/**
 * Sending a user back to /login after their session dies.
 *
 * Every caller has already lost the session (an unrecoverable 401, or a token
 * refresh that failed), so the only open question is where to return them once
 * they sign back in.
 */

const BASE_PATH = import.meta.env.VITE_BASE_PATH || ''

/** Leftovers keycloak-js parks in the URL on an OIDC callback. */
const OIDC_CALLBACK_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
]

export function loginPath(basePath: string = BASE_PATH): string {
  const base = basePath && !basePath.startsWith('/') ? `/${basePath}` : basePath
  return `${base}/login`
}

export function isOnLoginPage(
  pathname: string = window.location.pathname,
  basePath: string = BASE_PATH,
): boolean {
  const login = loginPath(basePath)
  // TanStack serves the route both ways; `includes` would also match a path
  // like /clients/$id/login-history.
  return pathname === login || pathname === `${login}/`
}

/**
 * The current URL, cleaned up for use as a `?redirect=` value.
 *
 * Drops the OIDC callback leftovers — keycloak-js parks `code`/`state` in the
 * URL and strips them a tick later, so capturing mid-flight would bake an
 * authorization code into a value that ends up in history and access logs —
 * and drops any existing `redirect` param so it cannot nest inside itself.
 */
export function captureRedirectTarget(
  href: string = window.location.href,
): string {
  const url = new URL(href)
  url.hash = ''
  url.searchParams.delete('redirect')
  for (const param of OIDC_CALLBACK_PARAMS) {
    url.searchParams.delete(param)
  }
  return url.toString()
}

/**
 * Hard-navigate to the login page, remembering where the user was.
 *
 * No-ops when already on /login: the `?redirect=` already there is the one
 * worth keeping, and re-assigning the same URL would cost a full document load
 * plus a Keycloak `prompt=none` round trip on every failed request.
 */
export function redirectToLogin(): void {
  if (isOnLoginPage()) return
  const redirect = encodeURIComponent(captureRedirectTarget())
  window.location.href = `${loginPath()}?redirect=${redirect}`
}
